import { useRef, useEffect } from "react";
import { Canvas } from "@react-three/fiber";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import CosmicPortal from "@/components/three/CosmicPortal";
import { IMAGES } from "@/data/content";

const ease = [0.22, 1, 0.36, 1];

export const WelcomeGate = ({ onEnter }) => {
  const mouse = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const handle = (e) => {
      mouse.current = {
        x: (e.clientX / window.innerWidth) * 2 - 1,
        y: -(e.clientY / window.innerHeight) * 2 + 1,
      };
    };
    window.addEventListener("mousemove", handle);
    return () => window.removeEventListener("mousemove", handle);
  }, []);

  return (
    <motion.div
      className="fixed inset-0 z-[90] flex items-center justify-center overflow-hidden bg-[#030303]"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, filter: "blur(12px)" }}
      transition={{ duration: 1.1, ease }}
      data-testid="welcome-gate"
    >
      <div className="absolute inset-0">
        <Canvas camera={{ position: [0, 0, 5.4], fov: 46 }} dpr={[1, 2]} gl={{ alpha: true }}>
          <CosmicPortal mouse={mouse} />
        </Canvas>
      </div>
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_28%,#030303_82%)]" />

      <Link
        to="/"
        className="pointer-events-auto absolute left-6 top-6 z-20 inline-flex items-center gap-2 text-[0.65rem] font-light uppercase tracking-[0.22em] text-white/50 transition-colors duration-300 hover:text-[#C5A059]"
        data-testid="welcome-back-publication"
      >
        <ArrowLeft size={14} /> Sybils &amp; Society
      </Link>

      <motion.div
        className="relative z-10 flex flex-col items-center px-6 text-center"
        initial={{ opacity: 0, y: 26 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.3, delay: 0.3, ease }}
      >
        <motion.img
          src={IMAGES.logo}
          alt="Sybils Society"
          className="h-28 w-auto object-contain [filter:drop-shadow(0_0_34px_rgba(197,160,89,0.35))] md:h-40"
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
        <p className="overline mt-8">Sybils Society — The Return of the Matriarchs</p>
        <h1 className="mt-4 font-serif text-[clamp(2rem,6vw,4rem)] font-light italic leading-[1.02] tracking-tight text-white">
          You stand at the <span className="text-[#C5A059]">threshold</span>.
        </h1>
        <p className="mt-5 max-w-sm text-sm font-light leading-relaxed text-white/55">
          What waits beyond the veil is meant to be felt. Turn your sound on, and
          enter with intention.
        </p>

        <button
          onClick={onEnter}
          className="cta-glow pointer-events-auto mt-10 rounded-full bg-[#C5A059] px-12 py-5 text-xs font-medium uppercase tracking-[0.3em] text-[#050505] transition-transform duration-300 hover:-translate-y-1"
          data-testid="welcome-enter-btn"
        >
          Enter
        </button>
        <p className="mt-6 font-mono text-[0.6rem] uppercase tracking-[0.24em] text-white/25">
          ✦ Sound on · Best with headphones ✦
        </p>
      </motion.div>
    </motion.div>
  );
};

export default WelcomeGate;
